import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, Gift } from "lucide-react";
import { submitLead } from "@/lib/api";

const SHOWN_KEY = "et_lead_popup_shown";
const DELAY_MS = 10000;

const phoneOk = (p) => /^[0-9+\-()\s]{6,20}$/.test(p);

/**
 * Site-wide lead-capture popup. Shows once per visitor (localStorage) 10s after
 * load OR on exit-intent, whichever comes first. Fields: Name, Mobile, City.
 */
export const LeadPopup = () => {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", city: "" });
  const [errors, setErrors] = useState({});
  const [company, setCompany] = useState(""); // honeypot
  const triggered = useRef(false);

  useEffect(() => {
    let alreadyShown = false;
    try { alreadyShown = localStorage.getItem(SHOWN_KEY) === "1"; } catch (e) {}
    if (alreadyShown) return;

    const trigger = () => {
      if (triggered.current) return;
      triggered.current = true;
      setOpen(true);
      try { localStorage.setItem(SHOWN_KEY, "1"); } catch (e) {}
      cleanup();
    };

    const timer = setTimeout(trigger, DELAY_MS);
    const onMouseOut = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget) trigger();
    };
    const cleanup = () => {
      clearTimeout(timer);
      document.removeEventListener("mouseout", onMouseOut);
    };
    document.addEventListener("mouseout", onMouseOut);
    return cleanup;
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2) e.name = "Please enter your name";
    if (!phoneOk(form.phone)) e.phone = "Enter a valid mobile number";
    if (!form.city.trim()) e.city = "Please enter your city";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (company.trim()) { setOpen(false); return; } // honeypot
    if (!validate()) return;
    setSubmitting(true);
    try {
      await submitLead({
        name: form.name.trim(),
        phone: form.phone.trim(),
        city: form.city.trim(),
        service_required: "WhatsApp Automation",
        message: "Offer lead via popup: FREE WhatsApp API for 1 month + FREE Meta Business Verification.",
        source: "popup",
      });
      setDone(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden" data-testid="lead-popup">
        {/* Header banner */}
        <div className="et-navy-band relative px-6 py-6 text-white">
          <div className="absolute inset-0 et-dot-grid opacity-[0.15]" />
          <div className="relative flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-[var(--et-accent)]"><Gift className="h-5 w-5" /></span>
            <div>
              <h3 className="font-display text-lg font-bold leading-tight">Claim Your Free Offer</h3>
              <p className="text-xs text-slate-300">FREE WhatsApp API for 1 month + FREE Meta Business Verification</p>
            </div>
          </div>
        </div>

        {done ? (
          <div className="px-6 py-8 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-50"><CheckCircle2 className="h-8 w-8 text-green-600" /></span>
            <h4 className="mt-4 font-display text-xl font-bold text-slate-900">Thank you!</h4>
            <p className="mt-2 text-sm text-slate-600">Our team will reach out to you shortly to activate your free offer.</p>
            <button onClick={() => setOpen(false)} data-testid="lead-popup-close" className="mt-5 w-full rounded-xl h-11 text-sm font-semibold bg-[var(--et-primary)] text-white hover:bg-[var(--et-primary-hover)] transition-colors">Close</button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="px-6 py-6 space-y-3.5" noValidate>
            <p className="text-sm text-slate-600">Share your details and our team will help you claim your free offer.</p>
            {/* honeypot */}
            <input type="text" tabIndex={-1} autoComplete="off" value={company} onChange={(e) => setCompany(e.target.value)} className="hidden" aria-hidden="true" />

            <Field label="Name" error={errors.name}>
              <Input data-testid="popup-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your full name" />
            </Field>
            <Field label="Mobile Number" error={errors.phone}>
              <Input data-testid="popup-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="10-digit mobile number" />
            </Field>
            <Field label="City" error={errors.city}>
              <Input data-testid="popup-city-input" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Your city" />
            </Field>
            <button type="submit" disabled={submitting} data-testid="popup-submit-button" className="w-full inline-flex items-center justify-center gap-2 rounded-xl h-11 text-sm font-semibold bg-[var(--et-primary)] text-white hover:bg-[var(--et-primary-hover)] transition-colors disabled:opacity-70">
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : "Claim Free Offer"}
            </button>
            <p className="text-[11px] text-slate-400 text-center">We respect your privacy. No spam, ever.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, error, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold text-slate-600">{label}</Label>
    {children}
    {error && <p className="text-xs text-red-500">{error}</p>}
  </div>
);
